import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const INK = "#1e2a3a";
const MUTED = "#6b7a8d";
const LINE = "#e6ecf3";
const BLUE = "#2563eb";
const INDIGO = "#6366f1";
const GREEN = "#0f9d70";
const AMBER = "#f0a92e";
const RED = "#e5484d";
const PURPLE = "#a855f7";
const SLATE = "#64748b";
const BG = "#f7f9fc";

const CAT_COLORS = {
  "Caste Census & Booth Committees": BLUE,
  "Internal Coordination & Factionalism": AMBER,
  "Leadership Vacancy & Absence": RED,
  "Other Local Matters": GREEN,
  "Candidate & Alignment Dynamics": PURPLE,
  "Wing & Organizational Setup": SLATE,
};
const REGION_COLORS = { Malwa: "#0e7fc0", Majha: GREEN, Doaba: INDIGO };

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: 18 };
const cardTitle = { fontSize: 16, fontWeight: 700, color: INK };
const cardSub = { fontSize: 12.5, color: MUTED, marginBottom: 14 };

const Donut = ({ counts }) => {
  const items = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const total = items.reduce((a, [, v]) => a + v, 0) || 1;
  // Geometry: donut centred in a wide viewBox so labels sit outside with leader lines.
  const W = 900, H = 430, CX = 450, CY = 215, R = 112, SW = 46;
  const rOuter = R + SW / 2;
  let angle = -Math.PI / 2; // start at 12 o'clock
  const segs = items.map(([name, value]) => {
    const sweep = (value / total) * Math.PI * 2;
    const mid = angle + sweep / 2;
    const seg = { name, value, start: angle, sweep, mid };
    angle += sweep;
    return seg;
  });

  const arcPath = (start, sweep) => {
    const x1 = CX + R * Math.cos(start), y1 = CY + R * Math.sin(start);
    const x2 = CX + R * Math.cos(start + sweep), y2 = CY + R * Math.sin(start + sweep);
    const large = sweep > Math.PI ? 1 : 0;
    return `M${x1},${y1} A${R},${R} 0 ${large} 1 ${x2},${y2}`;
  };

  // Stack labels on each side so they never overlap.
  const left = segs.filter((s) => Math.cos(s.mid) < 0).sort((a, b) => (CY + Math.sin(a.mid)) - (CY + Math.sin(b.mid)));
  const right = segs.filter((s) => Math.cos(s.mid) >= 0).sort((a, b) => (CY + Math.sin(a.mid)) - (CY + Math.sin(b.mid)));
  const place = (list, side) => {
    const step = 42;
    const startY = CY - ((list.length - 1) * step) / 2;
    return list.map((s, i) => ({
      ...s,
      side,
      lx: side === "left" ? 250 : 650,
      ly: startY + i * step,
      tx: side === "left" ? 240 : 660,
    }));
  };
  const labelled = [...place(left, "left"), ...place(right, "right")];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {segs.map((s) => (
        <path key={s.name} d={arcPath(s.start, s.sweep)} fill="none"
          stroke={CAT_COLORS[s.name] || SLATE} strokeWidth={SW} />
      ))}
      <text x={CX} y={CY - 2} textAnchor="middle" fontSize="34" fontWeight="700" fill={INK}>{total}</text>
      <text x={CX} y={CY + 20} textAnchor="middle" fontSize="12" fill={MUTED} letterSpacing="1.5">ISSUES</text>

      {labelled.map((s) => {
        const px = CX + rOuter * Math.cos(s.mid);
        const py = CY + rOuter * Math.sin(s.mid);
        const elbowX = s.side === "left" ? s.lx + 22 : s.lx - 22;
        return (
          <g key={`l-${s.name}`}>
            <polyline
              points={`${px},${py} ${elbowX},${s.ly} ${s.lx},${s.ly}`}
              fill="none" stroke="#9aa7b6" strokeWidth="1.1"
            />
            <text
              x={s.tx} y={s.ly + 4}
              textAnchor={s.side === "left" ? "end" : "start"}
              fontSize="13.5" fontWeight="700" fill={INK}
            >
              {s.name}: {s.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const HBars = ({ data, colorFor, max }) => {
  const top = max || Math.max(...data.map((d) => d.value), 1);
  return (
    <div>
      {data.map((d) => (
        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 92, textAlign: "right", fontSize: 12.5, color: INK, flexShrink: 0 }}>{d.label}</div>
          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 26, position: "relative" }}>
            <div style={{ width: `${(d.value / top) * 100}%`, background: colorFor(d.label), height: 26, borderRadius: 4 }} />
          </div>
          <div style={{ width: 38, fontSize: 13, fontWeight: 700, color: INK }}>{fmt(d.value)}</div>
        </div>
      ))}
    </div>
  );
};

const VBars = ({ data }) => {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 210, overflowX: "auto", paddingTop: 20 }}>
      {data.map((d) => (
        <div key={d.label} style={{ textAlign: "center", minWidth: 54, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 4 }}>{d.value}</div>
          <div style={{ background: BLUE, height: Math.max((d.value / max) * 130, 4), borderRadius: "3px 3px 0 0" }} />
          <div style={{ fontSize: 10, color: MUTED, marginTop: 6, height: 46, lineHeight: 1.2, wordBreak: "break-word" }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
};

const InterventionsDashboard = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [view, setView] = useState("analytics");
  const [detail, setDetail] = useState(null);

  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [acName, setAcName] = useState("");
  const [search, setSearch] = useState("");

  const authHeader = async () => {
    const token = await localforage.getItem("token");
    return { Authorization: "Bearer " + token };
  };

  const load = async (refresh) => {
    setLoading(true); setErr("");
    try {
      const headers = await authHeader();
      const res = await api.get(`${API_URL}/dashboards/interventions`, {
        headers, params: refresh ? { refresh: "1" } : {},
      });
      setRecords(res.data.records || []);
    } catch (e) {
      setErr(e.response?.data?.error || "Could not load the dashboard.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const regionCounts = useMemo(() => {
    const c = {};
    records.forEach((r) => { c[r.region] = (c[r.region] || 0) + 1; });
    return c;
  }, [records]);

  const districts = useMemo(
    () => [...new Set(records.filter((r) => !region || r.region === region).map((r) => r.district).filter(Boolean))].sort(),
    [records, region]
  );
  const acNames = useMemo(
    () => [...new Set(records
      .filter((r) => (!region || r.region === region) && (!district || r.district === district))
      .map((r) => r.acName).filter(Boolean))].sort(),
    [records, region, district]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((r) =>
      (!region || r.region === region) &&
      (!district || r.district === district) &&
      (!acName || r.acName === acName) &&
      (!q || [r.acName, r.district, r.halkaIncharge, r.halkaCoordinator, r.leaderInvolved, r.issueSummary, r.politicalActionable, r.brief]
        .some((v) => String(v || "").toLowerCase().includes(q)))
    );
  }, [records, region, district, acName, search]);

  const k = useMemo(() => ({
    districts: new Set(rows.map((r) => r.district).filter(Boolean)).size,
    regions: new Set(rows.map((r) => r.region).filter(Boolean)).size,
    acs: new Set(rows.map((r) => r.acName).filter(Boolean)).size,
    interventions: rows.length,
    inchargeOk: rows.filter((r) => !r.inchargeVacant).length,
    inchargeVacant: rows.filter((r) => r.inchargeVacant).length,
    coordOk: rows.filter((r) => !r.coordinatorVacant).length,
    coordVacant: rows.filter((r) => r.coordinatorVacant).length,
    actionables: rows.filter((r) => r.politicalActionable).length,
  }), [rows]);

  const catCounts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.category] = (c[r.category] || 0) + 1; });
    return c;
  }, [rows]);

  const byRegion = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.region] = (c[r.region] || 0) + 1; });
    return Object.entries(c).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }, [rows]);

  const topDistricts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { if (r.district) c[r.district] = (c[r.district] || 0) + 1; });
    return Object.entries(c).map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value).slice(0, 10);
  }, [rows]);

  const regionOverview = useMemo(() => {
    const by = {};
    rows.forEach((r) => {
      const z = r.region || "Unknown";
      if (!by[z]) by[z] = { region: z, acs: 0, coordVacant: 0, inchargeVacant: 0, actionables: 0 };
      by[z].acs += 1;
      if (r.coordinatorVacant) by[z].coordVacant += 1;
      if (r.inchargeVacant) by[z].inchargeVacant += 1;
      if (r.politicalActionable) by[z].actionables += 1;
    });
    return Object.values(by).sort((a, b) => b.acs - a.acs);
  }, [rows]);

  const reset = () => { setRegion(""); setDistrict(""); setAcName(""); setSearch(""); };
  const sel = { padding: "8px 11px", border: `1px solid ${LINE}`, borderRadius: 9, fontSize: 13, background: "#fff", color: INK };

  const Kpi = ({ title, value, sub, children }) => (
    <div style={card}>
      <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 6 }}>{title}</div>
      {children || (
        <>
          <div style={{ fontSize: 30, fontWeight: 800, color: INK, lineHeight: 1.1 }}>{value}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{sub}</div>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Dashboard />
      <div style={{ background: BG, minHeight: "100vh", padding: 18 }}>
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>

          <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 21, fontWeight: 800, color: INK }}>Region Wise Intervention Dashboard</div>
              <div style={{ fontSize: 13, color: MUTED }}>Assembly Constituency leadership tracking, field intelligence &amp; political actionables</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
                {[["analytics", "Analytics"], ["matrix", "Matrix"]].map(([id, name]) => (
                  <button key={id} type="button" onClick={() => setView(id)}
                    style={{
                      background: view === id ? "#fff" : "transparent", color: view === id ? INK : MUTED,
                      border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13,
                    }}>{name}</button>
                ))}
              </div>
              <button type="button" onClick={() => load(true)}
                style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Refresh
              </button>
            </div>
          </div>

          {/* KPI row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 14 }}>
            <Kpi title="Districts Covered" value={k.districts} sub={`Across ${k.regions} Regions`} />
            <Kpi title="Assemblys Covered" value={<span style={{ color: BLUE }}>{k.acs}</span>} sub="Constituencies Mapped" />
            <Kpi title="Tracked Interventions" value={k.interventions} sub="Logged Field Reports" />
            <Kpi title="Leadership Status">
              <div style={{ fontSize: 13, marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ color: MUTED }}>Halka Coordinator:</span>
                  <span><strong style={{ color: GREEN }}>{k.coordOk}</strong> <span style={{ color: MUTED }}>/</span> <strong style={{ color: RED }}>{k.coordVacant} vacant</strong></span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: MUTED }}>Halka Incharge:</span>
                  <span><strong style={{ color: GREEN }}>{k.inchargeOk}</strong> <span style={{ color: MUTED }}>/</span> <strong style={{ color: RED }}>{k.inchargeVacant} vacant</strong></span>
                </div>
              </div>
            </Kpi>
            <Kpi title="Political Actionables" value={<span style={{ color: GREEN }}>{k.actionables}</span>} sub="Action Recommendations" />
          </div>

          {/* filters */}
          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: MUTED, letterSpacing: "0.5px" }}>REGION:</span>
                {[["", "All", records.length], ...Object.entries(regionCounts).map(([r, c]) => [r, r, c])].map(([val, name, count]) => (
                  <button key={name} type="button" onClick={() => { setRegion(val); setDistrict(""); setAcName(""); }}
                    style={{
                      background: region === val ? BLUE : "#f1f5f9", color: region === val ? "#fff" : INK,
                      border: "none", borderRadius: 99, padding: "6px 14px", fontWeight: 600, cursor: "pointer", fontSize: 12.5,
                    }}>
                    {name} <span style={{ opacity: 0.75, marginLeft: 4 }}>{count}</span>
                  </button>
                ))}
              </div>
              <input placeholder="Search AC, leader, actionable, issue…" value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...sel, minWidth: 280, flex: "0 1 340px" }} />
            </div>
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: MUTED }}>District:</span>
              <select value={district} onChange={(e) => { setDistrict(e.target.value); setAcName(""); }} style={sel}>
                <option value="">All Districts</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <span style={{ fontSize: 13, color: MUTED }}>AC Name:</span>
              <select value={acName} onChange={(e) => setAcName(e.target.value)} style={sel}>
                <option value="">All Constituencies (AC)</option>
                {acNames.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <span style={{ fontSize: 12.5, color: MUTED, marginLeft: "auto" }}>
                Showing <strong style={{ color: INK }}>{rows.length}</strong> of {records.length}
              </span>
              <button type="button" onClick={reset}
                style={{ background: "none", border: "none", color: BLUE, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
                Reset All Filters
              </button>
            </div>
          </div>

          {err && <p style={{ color: RED, textAlign: "center", fontWeight: 600 }}>{err}</p>}
          {loading && <p style={{ textAlign: "center", color: MUTED }}>Loading dashboard…</p>}

          {!loading && !err && view === "analytics" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={cardTitle}>Issue Summary Breakdown</div>
                      <div style={cardSub}>Donut visualization of grounded issue patterns with category counts</div>
                    </div>
                    <span style={{ background: "#eaf2fe", color: BLUE, borderRadius: 8, padding: "5px 11px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                      {rows.length} Active Issues
                    </span>
                  </div>
                  <Donut counts={catCounts} />
                </div>
                <div style={card}>
                  <div style={cardTitle}>Interventions by Region</div>
                  <div style={cardSub}>Regional breakdown across Punjab</div>
                  <HBars data={byRegion} colorFor={(l) => REGION_COLORS[l] || BLUE} />
                  <div style={{ display: "flex", justifyContent: "space-around", borderTop: `1px solid ${LINE}`, paddingTop: 14, marginTop: 14 }}>
                    {byRegion.map((r) => (
                      <div key={r.label} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 12.5, color: MUTED }}>{r.label}</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: INK }}>{r.value} ACs</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={cardTitle}>Top Districts by Tracked Interventions</div>
                      <div style={cardSub}>Ground constituencies logged in the dataset requiring leadership review</div>
                    </div>
                    <span style={{ background: "#eaf2fe", color: BLUE, borderRadius: 8, padding: "5px 11px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                      {k.districts} Districts Tracked
                    </span>
                  </div>
                  <VBars data={topDistricts} />
                </div>
                <div style={card}>
                  <div style={cardTitle}>Constituency Leadership Appointment Status</div>
                  <div style={cardSub}>Direct tally of Appointed vs Vacant roles across tracked ACs</div>
                  {[["Halka Incharge", k.inchargeOk, k.inchargeVacant], ["Halka Coordinator", k.coordOk, k.coordVacant]].map(([name, ok, vac]) => {
                    const top = Math.max(ok + vac, 1);
                    return (
                      <div key={name} style={{ marginBottom: 22 }}>
                        <div style={{ fontSize: 13, color: INK, marginBottom: 8, fontWeight: 600 }}>{name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 24 }}>
                            <div style={{ width: `${(ok / top) * 100}%`, background: GREEN, height: 24, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 12.5, color: GREEN, fontWeight: 700, whiteSpace: "nowrap", width: 96 }}>{ok} Appointed</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 4, height: 24 }}>
                            <div style={{ width: `${(vac / top) * 100}%`, background: RED, height: 24, borderRadius: 4 }} />
                          </div>
                          <span style={{ fontSize: 12.5, color: RED, fontWeight: 700, whiteSpace: "nowrap", width: 96 }}>{vac} Vacant</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ ...card, marginTop: 14 }}>
                <div style={cardTitle}>Regional Overview Breakdown</div>
                <div style={cardSub}>Constituencies, leadership vacancies, and actionable readiness grouped by Region</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
                  {regionOverview.map((z) => (
                    <div key={z.region} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <strong style={{ fontSize: 15.5, color: INK }}>{z.region}</strong>
                        <span style={{ background: "#eaf2fe", color: BLUE, borderRadius: 8, padding: "4px 11px", fontSize: 12, fontWeight: 700 }}>{z.acs} ACs</span>
                      </div>
                      {[["Halka Coordinator Vacant:", z.coordVacant, PURPLE],
                        ["Halka Incharge Vacant:", z.inchargeVacant, RED],
                        ["Political Actionables:", z.actionables, GREEN]].map(([t, v, c]) => (
                        <div key={t} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0" }}>
                          <span style={{ color: MUTED }}>{t}</span>
                          <strong style={{ color: c }}>{v}</strong>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {!loading && !err && view === "matrix" && (
            <div style={{ ...card, padding: 0 }}>
              <div style={{ overflowX: "auto", maxHeight: 660, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: "#f2f5fa" }}>
                      {["AC Details", "Region / District", "Halka Incharge", "Halka Coordinator", "Issue Summary", "Political Actionable", "Actions"].map((h) => (
                        <th key={h} style={{ padding: "12px 14px", textAlign: "left", borderBottom: `1px solid ${LINE}`, color: MUTED, fontSize: 11, letterSpacing: "0.5px", textTransform: "uppercase", position: "sticky", top: 0, background: "#f2f5fa", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.acNo}-${i}`} style={{ borderBottom: `1px solid ${LINE}`, verticalAlign: "top" }}>
                        <td style={{ padding: "13px 14px", minWidth: 130 }}>
                          <div style={{ fontWeight: 700, color: INK }}>{r.acName}</div>
                          <span style={{ display: "inline-block", marginTop: 4, background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", fontSize: 11, color: MUTED }}>AC #{r.acNo}</span>
                        </td>
                        <td style={{ padding: "13px 14px", minWidth: 110 }}>
                          <div style={{ fontWeight: 600, color: INK }}>{r.district}</div>
                          <div style={{ fontSize: 11.5, color: MUTED }}>{r.region}</div>
                        </td>
                        <td style={{ padding: "13px 14px", minWidth: 150, color: r.inchargeVacant ? PURPLE : INK }}>
                          {r.inchargeVacant ? "Not Appointed" : r.halkaIncharge}
                        </td>
                        <td style={{ padding: "13px 14px", minWidth: 150, color: r.coordinatorVacant ? PURPLE : INK }}>
                          {r.coordinatorVacant ? "Not Appointed" : r.halkaCoordinator}
                        </td>
                        <td style={{ padding: "13px 14px", minWidth: 220, color: INK }}>
                          {r.issueSummary}
                          <div style={{ marginTop: 5 }}>
                            <span style={{ background: `${CAT_COLORS[r.category] || SLATE}15`, color: CAT_COLORS[r.category] || SLATE, borderRadius: 6, padding: "2px 8px", fontSize: 10.5, fontWeight: 700 }}>
                              {r.category}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "13px 14px", minWidth: 300, color: "#374151", lineHeight: 1.5 }}>
                          {r.politicalActionable || <span style={{ color: MUTED }}>—</span>}
                        </td>
                        <td style={{ padding: "13px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <button type="button" title="View full details" onClick={() => setDetail(r)}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: BLUE, padding: "2px 6px" }}>
                            &#128065;
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {detail && (
            <div
              onClick={() => setDetail(null)}
              style={{
                position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20, zIndex: 1000,
              }}
            >
              <div onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff", borderRadius: 16, width: "min(860px, 100%)",
                  maxHeight: "88vh", display: "flex", flexDirection: "column",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
                }}>
                {/* header */}
                <div style={{ padding: "18px 22px", borderBottom: `1px solid ${LINE}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ background: "#eaf2fe", color: BLUE, borderRadius: 7, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                          AC #{detail.acNo}
                        </span>
                        <span style={{ fontSize: 13, color: MUTED }}>{detail.district} • {detail.region}</span>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: INK }}>{detail.acName} Constituency</div>
                    </div>
                    <button type="button" onClick={() => setDetail(null)}
                      style={{ background: "none", border: "none", fontSize: 22, color: MUTED, cursor: "pointer", lineHeight: 1 }}>
                      &times;
                    </button>
                  </div>
                </div>

                {/* body */}
                <div style={{ padding: "18px 22px", overflowY: "auto" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 14 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: MUTED, letterSpacing: "0.6px", marginBottom: 6 }}>HALKA IN-CHARGE</div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: detail.inchargeVacant ? PURPLE : INK }}>
                        {detail.inchargeVacant ? "Not Appointed" : detail.halkaIncharge}
                      </div>
                    </div>
                    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 14 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: MUTED, letterSpacing: "0.6px", marginBottom: 6 }}>HALKA COORDINATOR</div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: detail.coordinatorVacant ? PURPLE : INK }}>
                        {detail.coordinatorVacant ? "Not Appointed" : detail.halkaCoordinator}
                      </div>
                    </div>
                  </div>

                  {detail.leaderInvolved ? (
                    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: MUTED, letterSpacing: "0.6px", marginBottom: 6 }}>LEADERS &amp; STAKEHOLDERS INVOLVED</div>
                      <div style={{ fontSize: 14, color: INK, lineHeight: 1.5 }}>{detail.leaderInvolved}</div>
                    </div>
                  ) : null}

                  <div style={{ fontSize: 11, fontWeight: 700, color: INK, letterSpacing: "0.6px", marginBottom: 7 }}>ISSUE SUMMARY</div>
                  <div style={{ background: "#f4f8fd", border: `1px solid ${LINE}`, borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 14, color: INK, lineHeight: 1.55 }}>
                    {detail.issueSummary || "—"}
                    <div style={{ marginTop: 8 }}>
                      <span style={{ background: `${CAT_COLORS[detail.category] || SLATE}18`, color: CAT_COLORS[detail.category] || SLATE, borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 }}>
                        {detail.category}
                      </span>
                    </div>
                  </div>

                  {detail.brief ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: INK, letterSpacing: "0.6px", marginBottom: 7 }}>DETAILED FIELD INTELLIGENCE &amp; CONTEXT</div>
                      <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 15, marginBottom: 16, fontSize: 13.5, color: "#374151", lineHeight: 1.65 }}>
                        {detail.brief}
                      </div>
                    </>
                  ) : null}

                  {detail.politicalActionable ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: BLUE, letterSpacing: "0.6px", marginBottom: 7 }}>RECOMMENDED POLITICAL ACTIONABLE</div>
                      <div style={{ background: "#f2f7ff", border: `1px solid #d6e6ff`, borderRadius: 10, padding: 15, fontSize: 13.5, color: "#1e3a5f", lineHeight: 1.65 }}>
                        {detail.politicalActionable}
                      </div>
                    </>
                  ) : null}
                </div>

                {/* footer */}
                <div style={{ padding: "14px 22px", borderTop: `1px solid ${LINE}`, display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" onClick={() => setDetail(null)}
                    style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 9, padding: "9px 22px", fontWeight: 600, cursor: "pointer", fontSize: 13.5 }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterventionsDashboard;
